/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import useForm from '../../hooks/useForm';

import StyledButton from '../buttons/StyledButton';
import Field from '../fields/Field';
import FormGroup from '../fields/FormGroup';
import CheckIcon from '../icons/Check';

import styles from '../../../styles/partials/form.module.css';

const propTypes = {
    action: PropTypes.string,
    fields: PropTypes.arrayOf(PropTypes.shape({})),
    value: PropTypes.shape({}),
    submitLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    submitDescription: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    callback: PropTypes.func,
    disabled: PropTypes.bool,
    centered: PropTypes.bool,
    onComplete: PropTypes.func,
    className: PropTypes.string,
};

const Form = ({
    action = null,
    callback = null,
    value = null,
    fields = null,
    submitLabel = null,
    submitDescription = null,
    disabled = false,
    centered = false,
    onComplete = null,
    className = null,
}) => {
    const [internalValue, setValue] = useState(value);
    useEffect(() => {
        setValue(value);
    }, [value]);

    const finalPostForm = useCallback(
        (act, data) => (callback !== null ? callback(data) : null),
        [callback],
    );
    const withoutPost = finalPostForm === null;

    const fieldNames = useMemo(() => (fields || []).map((field) => field.name), [fields]);

    const {
        fields: formFields,
        onSubmit,
        loading,
        success,
        // errors,
        generalError,
    } = useForm({
        postForm: finalPostForm,
        fields: fieldNames,
        onComplete,
        value: internalValue,
        setValue,
    });

    return (
        <form
            className={classNames([
                styles.container,
                { [styles.centered]: centered, [className]: className !== null },
            ])}
            action={action}
            onSubmit={onSubmit}
            disabled={disabled || withoutPost}
        >
            <div className={styles.fields}>
                {(fields || []).map((field) => {
                    const { name = null, label = null, type = null } = field || {};
                    const {
                        onChange = null,
                        value: fieldValue = null,
                        errors = null,
                    } = formFields[field.name] || {};
                    const { help = null } = field || {};
                    return (
                        <FormGroup
                            key={`field-${name}`}
                            name={name}
                            label={type !== 'checkbox' ? label : null}
                            errors={errors}
                            help={help}
                        >
                            <Field
                                {...field}
                                onChange={onChange}
                                value={fieldValue}
                                errors={errors}
                            />
                        </FormGroup>
                    );
                })}
            </div>
            {submitDescription !== null ? (
                <div className={styles.description}>{submitDescription}</div>
            ) : null}
            <StyledButton
                className={styles.submitButton}
                type="submit"
                loading={loading}
                disabled={loading || success || disabled || withoutPost}
            >
                {submitLabel || (
                    <FormattedMessage defaultMessage="Submit" description="Button label" />
                )}
                {success ? <CheckIcon className={styles.icon} /> : null}
            </StyledButton>
            {generalError !== null ? (
                <div className={styles.generalError}>{generalError}</div>
            ) : null}
        </form>
    );
};

Form.propTypes = propTypes;

export default Form;
